/**
 * Shared types for the V2 PRATUS AI drawer. These mirror the (untyped) shapes
 * that flow through `dchatSlice` / `useDisasterChats` so the V2 UI can be
 * strict without changing what is sent to COMS.
 */
import type { ChatActionResult } from '../ActionResultCard';
import type { PendingConfirmation } from '../agentConfirm/types';
import type { SelectedLayerForAI } from '../utils/prefetchLayerFeaturesForAI';
import type { ChatAttachment } from './chatAttachments';

export type AgentMode = 'ask' | 'agent_auto' | 'agent_confirm';

/** Start/end window for SerpAPI Web search (ISO strings). */
export type WebDateRange = {
  start: string;
  end: string;
};

/** One entry of the `realtimeSelections` array sent with each message. */
export interface RealtimeSelection {
  source: string;
  id?: string;
  description?: string;
  customData?: Record<string, unknown>;
  selectedFiles?: string[];
  selectedLayers?: SelectedLayerForAI[];
  filterByDatetime?: boolean;
  seeristRequest?: Record<string, unknown>;
  /** When set, Web search is limited to this window via SerpAPI `tbs`. */
  webDateRange?: WebDateRange;
  [key: string]: unknown;
}

export interface ChatMessage {
  role: string;
  content: string;
  timestamp?: string | Date;
  realtimeSelections?: RealtimeSelection[];
  selectedDatetime?: string;
  annotations?: unknown[];
  actions?: ChatActionResult[];
  /** Images the user sent with this message. */
  attachments?: ChatAttachment[];
}

/** Server-side run status persisted on the chat row. */
export type DChatServerStatus =
  | 'Planning'
  | 'Gathering Data'
  | 'Executing Tools'
  | 'PendingConfirmation'
  | 'Complete'
  | 'Error'
  | string;

export interface DChat {
  id?: string;
  name?: string;
  status?: DChatServerStatus;
  messages?: ChatMessage[];
  mode?: AgentMode;
  pending_confirmation?: PendingConfirmation[];
  user_guid?: string;
  current_sitrep_id?: string | null;
  initialBlueprint?: ChatBlueprint;
  err_msg?: string;
}

export interface ChatBlueprint {
  title: string;
  description: string;
  settings?: { datasources?: RealtimeSelection[] };
}

/** Client-side run status kept at the `dchat` slice root. */
export type DChatClientStatus =
  | 'idle'
  | 'loading'
  | 'ongoing'
  | 'succeeded'
  | 'failed'
  | 'pending_confirmation';

export const SERVER_IN_PROGRESS_STATUSES: ReadonlySet<string> = new Set([
  'Planning',
  'Gathering Data',
  'Executing Tools',
]);
